import React from "react";
import { Table, Tag, Button, Space, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import FinanceService from "../../../dependencies/custom/financeService";

/**
 * Transport Fees Table
 * Displays all transport fees with student info and payment status
 */
const TransportFeesTable = ({
  data = [],
  loading = false,
  onView,
  pagination = {},
  onPaginationChange,
}) => {
  const columns = [
    {
      title: "Student",
      key: "studentName",
      render: (_, record) => {
        // Use studentName directly if available, otherwise combine firstName + lastName
        const studentName =
          record.studentName ||
          `${record.firstName || record.first_name || ""} ${record.lastName || record.last_name || ""}`.trim();
        const spc = record.studentSpc || record.spcCode || record.spc || "";
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{studentName}</div>
            <div style={{ fontSize: "12px", color: "#888" }}>{spc}</div>
          </div>
        );
      },
      width: 200,
    },
    {
      title: "Bus",
      key: "busInfo",
      render: (_, record) => {
        const bus = record.busInfo;
        if (!bus) return <span style={{ color: "#ccc" }}>—</span>;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{bus.busNumber}</div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {bus.plateNumber}
            </div>
          </div>
        );
      },
      width: 140,
    },
    {
      title: "Route",
      key: "routeName",
      dataIndex: "routeName",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {record.transportTypeName} - TPC: {record.tpc}
          </div>
        </div>
      ),
      width: 180,
    },
    {
      title: "Term",
      key: "termName",
      dataIndex: "termName",
      width: 100,
    },
    {
      title: "Amount Due",
      key: "amountDue",
      dataIndex: "amountDue",
      render: (amount) => FinanceService.formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Paid",
      key: "amountPaid",
      dataIndex: "amountPaid",
      render: (amount) => FinanceService.formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Balance",
      key: "balance",
      dataIndex: "balance",
      render: (balance) => (
        <span
          style={{
            fontWeight: 500,
            color: balance > 0 ? "#f5222d" : "#52c41a",
          }}
        >
          {FinanceService.formatCurrency(balance)}
        </span>
      ),
      align: "right",
      width: 120,
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => {
        const colors = {
          Pending: "orange",
          "Partially Paid": "blue",
          "Fully Paid": "green",
          Overdue: "red",
        };
        return <Tag color={colors[status] || "default"}>{status}</Tag>;
      },
      width: 120,
    },
    {
      title: "Date",
      key: "createdAt",
      dataIndex: "createdAt",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onView && onView(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey={(record) => record.transportFeeAssignmentId || record.id}
      loading={loading}
      pagination={{
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 10,
        total: pagination.total || 0,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} transport fee(s)`,
        responsive: true,
        onChange: (page, pageSize) =>
          onPaginationChange && onPaginationChange(page, pageSize),
      }}
      scroll={{ x: 1200 }}
      size="large"
      style={{ marginTop: 16 }}
    />
  );
};

export default TransportFeesTable;
